import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import UndoIcon from "./../../../../../images/undo-icon.png";
import { faPrint } from "@fortawesome/free-solid-svg-icons";
import { useSelector } from "react-redux";
import {
  selectState,
  setReturnPopover,
  getOutputForEdit,
  getOutputForPrint,
  updateStockOutput,
  updateOutputListItemTemporarily,
  requestDeletingOutput,
  setEditPopover,
  setEditDetailsPopover
} from "../outputSlice";
import { FormatDateShort } from "utils/dateUtils";
import { useMemo, useRef, useState } from "react";
import PrintBillOfLanding from "./PrintBillOfLanding";
import PrintRequisition from "./PrintRequisition";
import { useAppDispatch } from "hooks/hooks";
import ReturnOutput from "./ReturnOutput";
import Popover from "components/Popover/Popover";
import InfoTooltip from "components/Popup/InfoTooltip";
import { IOutputListItem } from "Dashboard/scenes/Stock/domain/IOutputListItem";
import { IStockOutputUpdate } from "Dashboard/scenes/Stock/domain/IStockOutputUpdate";
import { debounce } from "lodash";
import { toast } from "react-toastify";
import { IOutput } from "Dashboard/scenes/Stock/domain/IOutput";
import { FormatNumber, FormatNumberWithUnitOrNull } from "utils/numberUtils";
import { selectState as selectStateAuth } from "authentication/main.slice";
import { hasFullAccessTo, hasWriteAccessTo } from "utils/authUtil";
import { STOCK_MODULE_NAME } from "Dashboard/scenes/Stock/domain/StockModule";
import { usePagePrint } from "hooks/usePagePrint";
import { grey } from "@mui/material/colors";
import CancelIcon from "@mui/icons-material/Cancel";
import DeleteOutputModal from "./DeleteOutputModal";
import { CiEdit } from "react-icons/ci";
import EditOutput from "./EditOutput";
import EditOutputDetails from "./EditOutputDetails";
import { buildAuditTooltip } from "utils/auditUtil";

interface IProps {
  lastTableElementRef?: any;
  handleScroll: (e: React.UIEvent<HTMLDivElement>) => void,
}

const TopSection = ({ lastTableElementRef, handleScroll }: IProps) => {
  const { outputListItems, returnPopover, editPopover, editDetailsPopover } = useSelector(selectState);
  const { user } = useSelector(selectStateAuth);
  const componentRefRequisition = useRef<HTMLDivElement>(null);
  const componentRefBillOfLanding = useRef<HTMLDivElement>(null);
  const [inputValues, setInputValues] = useState<{ [inputId: number]: string }>({});
  const [clickOutsideReturnPopoverActive, setClickOutsideReturnPopoverActive] = useState<boolean>(true);
  const [clickOutsideEditPopoverActive, setClickOutsideEditPopoverActive] = useState<boolean>(true);
  const [clickOutsideEditDetailsPopoverActive, setClickOutsideEditDetailsPopoverActive] = useState<boolean>(true);
  const [outputItemNote, setOutputItemNote] = useState("");
  const [skipCreatingReturnOutput, setSkipCreatingReturnOutput] = useState(false);
  const [outputIdToPrintRequisition, setOutputIdToPrintRequisition] = useState<number | null>(null);
  const [outputIdToPrintBillOfLanding, setOutputIdToPrintBillOfLanding] = useState<number | null>(null);

  const dispatch = useAppDispatch();
  const handlePrintRequisition = usePagePrint(
    componentRefRequisition, { landscape: true, margins: "0" });

  const handlePrintBillOfLanding = usePagePrint(
    componentRefBillOfLanding, { landscape: true, margins: "0" });

  const handleCloseReturnOutputPopover = () => {
    if (clickOutsideReturnPopoverActive) {
      dispatch(setReturnPopover({ isOpen: false }));
    }
  };

  const handleCloseEditOutputPopover = () => {
    if (clickOutsideEditPopoverActive) {
      dispatch(setEditPopover({ isOpen: false }));
    }
  };

  const handleCloseEditDetailsPopover = () => {
    if (clickOutsideEditDetailsPopoverActive) {
      dispatch(setEditDetailsPopover({ isOpen: false }));
    }
  };

  const onChangeBasicField = (data: IStockOutputUpdate) => {
    dispatch(updateOutputListItemTemporarily(data));
    updateBasicFieldWithDelay(data);
  };

  const updateBasicFieldWithDelay = useMemo(
    () => debounce(async (data: IStockOutputUpdate) => {
      try {
        await dispatch(updateStockOutput(data)).unwrap();
        toast.success("Izmena je uspešno izvršena.");
      } catch (status: any) {
        if (status === 404 || status === 403 || status === 400) {
          toast.error("Izmena nije moguća zbog neočekivane greške");
        }
      }
    }, 1000),
    [dispatch]
  );

  const printRequisition = (outputListItemToPrint: IOutputListItem) => {
    setOutputIdToPrintRequisition(outputListItemToPrint.outputId);
    dispatch(getOutputForPrint(outputListItemToPrint.outputId));
  };

  const doPrintRequisition = (outputListItemToPrint: IOutput | undefined) => {
    if (outputIdToPrintRequisition === outputListItemToPrint?.outputId) {
      handlePrintRequisition();
      setOutputIdToPrintRequisition(null);
    }
  };

  const printBillOfLanding = (outputListItemToPrint: IOutputListItem) => {
    setOutputIdToPrintBillOfLanding(outputListItemToPrint.outputId);
    dispatch(getOutputForPrint(outputListItemToPrint.outputId));
  };

  const doPrintBillOfLanding = (outputListItemToPrint: IOutput | undefined) => {
    if (outputIdToPrintBillOfLanding === outputListItemToPrint?.outputId) {
      handlePrintBillOfLanding();
      setOutputIdToPrintBillOfLanding(null);
    }
  }

  const whereOnBill = (outputListItem: IOutputListItem) => {
    let projectOfficial = outputListItem.project ? outputListItem.project.projectName : "";
    return outputListItem.partner
      ? outputListItem.partner.fullName
      : outputListItem.projectOverride
        ? outputListItem.projectOverride.projectName
        : projectOfficial;
  }

  const whereForReal = (outputListItem: IOutputListItem) => {
    let projectOfficial = outputListItem.project ? outputListItem.project.projectName : "";
    return outputListItem.partner
      ? outputListItem.partner.fullName
      : outputListItem.projectOverride
        ? `${projectOfficial} (na otpremnici: ${outputListItem.projectOverride.projectName})`
        : outputListItem.outputForEmployee
          ? `${projectOfficial} (za zaposlenog: ${outputListItem.outputForEmployee.name})`
          : projectOfficial;
  };

  return (
    <>
      <div className="g-grid g-header">
        <div className="row">
          <div className="col separator-line"></div>
          <div className="col lowercase center separator-line">
            šifra a
          </div>
          <div className="col lowercase center separator-line">
            br. otpremnice
          </div>
          <div className="col lowercase separator-line table-header-padding">
            ime
          </div>
          <div className="col lowercase center separator-line">količina iz.</div>
          <div className="col lowercase center separator-line">mera</div>
          <div className="col lowercase center separator-line">cena izdatog</div>
          <div className="col lowercase center separator-line">datum izlaza</div>
          <div className="col lowercase center separator-line">
            odvezao i registracija
          </div>
          <div className="col lowercase center separator-line">gde je otišlo</div>
          <div className="col lowercase center separator-line">dobavljač</div>
          <div className="col lowercase separator-line table-header-padding">
            napomena
          </div>
        </div>
      </div>

      <div className="g-grid g-content" onScroll={handleScroll}>
        {outputListItems &&
          outputListItems.map((outputListItem, outputItemIndex) => {
            const isLast = outputItemIndex === outputListItems.length - 1;
            const hasReturnButton = hasWriteAccessTo(user, STOCK_MODULE_NAME)
              && outputListItem.orderNumberWithinOutput === 1
              && !outputListItem.returnedBillOfLandingId;
            const hasDeleteButton = false && hasFullAccessTo(user, STOCK_MODULE_NAME)
              && outputListItem.orderNumberWithinOutput === 1;
            const hasEditButton = hasFullAccessTo(user, STOCK_MODULE_NAME)
              && outputListItem.orderNumberWithinOutput === 1;
            return (
              <div
                className={`row${outputListItem.orderNumberWithinOutput === 1 ? ' firstRow' : ''}`}
                key={outputListItem.outputItemId}
                ref={isLast ? lastTableElementRef : undefined}
              >
                <div className="col">
                  <div className="bg-item bg-transparent center">
                    {outputListItem.orderNumberWithinOutput === 1
                      ? (
                        <InfoTooltip tooltip="Štampaj otpremnicu" element="span"
                          onClick={() => printBillOfLanding(outputListItem)}>
                          <FontAwesomeIcon fontSize={13} icon={faPrint} />
                        </InfoTooltip>
                      )
                      : (<span className="no-print-space"></span>)}
                    {outputListItem.orderNumberWithinOutput === 1 && outputListItem.requisitionId
                      ? (
                        <InfoTooltip tooltip={`Štampaj trebovanje ${outputListItem.requisitionId}`} element="span"
                          onClick={() => printRequisition(outputListItem)}>
                          <FontAwesomeIcon fontSize={13} icon={faPrint} />
                        </InfoTooltip>

                      )
                      : (<span className="no-print-space"></span>)}
                    <span className="xxx">
                      <input type="checkbox" checked={outputListItem.xxx} readOnly />
                      <span className="special-ch"></span>
                    </span>
                  </div>
                </div>
                <div className="col light center">
                  <input type="text"
                    className={`bg-item-border-light bg-transparent light center${hasWriteAccessTo(user, STOCK_MODULE_NAME) ? ' hoverBorder' : ''}`}
                    value={outputListItem.code}
                    onChange={(e) => {
                      onChangeBasicField({ outputId: outputListItem.outputId, outputItemId: outputListItem.outputItemId, inputItemCode: e.target.value })
                    }}
                    disabled={!hasWriteAccessTo(user, STOCK_MODULE_NAME)}
                  />
                </div>
                <div className="col center">
                  <InfoTooltip tooltip={`${outputListItem.billOfLandingId}`} element="span"
                    classesProp="bg-item spec-danger-span center">
                    {outputListItem.billOfLandingId}
                  </InfoTooltip>
                  <span className={`light ${!hasReturnButton ? "marginFix" : ""}`}>{outputListItem.orderNumberWithinOutput}</span>
                  {hasReturnButton &&
                    <InfoTooltip tooltip="Povraćaj" element="span"
                      classesProp="bg-item bg-transparent icon" onClick={(e) => {
                        dispatch(setReturnPopover({
                          isOpen: true,
                          left: e.clientX,
                          top: e.clientY,
                          offsetLeft: 20,
                          offsetTop: -100
                        }));
                        dispatch(getOutputForEdit(outputListItem.outputId));
                      }
                      }>
                      <img
                        className="undo-icon"
                        src={UndoIcon}
                        alt=""
                      />
                    </InfoTooltip>}
                </div>

                <div className="col name">
                  <div className="bg-item overflowMinWidth">
                    <InfoTooltip
                      tooltip={buildAuditTooltip(outputListItem.name, outputListItem)}
                      classesProp="bg-text-overflow"
                    >
                      {outputListItem.name}
                    </InfoTooltip>
                  </div>
                  <label className="bg-item">{outputListItem.repository?.shortName}</label>
                </div>
                <div className="col">
                  <div className="bg-item right danger">
                    <span>
                      {FormatNumber(outputListItem.quantityAmount)}
                    </span>
                  </div>
                </div>
                <div className="col light">
                  <div className="bg-item bg-transparent">
                    <span>{outputListItem.unit.name}</span>
                  </div>
                </div>
                <div className="col">
                  <div className="bg-item overflowMinWidth right danger">
                    <InfoTooltip
                      element="span"
                      classesProp="bg-text-overflow"
                      tooltip={`${FormatNumberWithUnitOrNull(outputListItem.totalCost, 'rsd')}`}>
                      {FormatNumberWithUnitOrNull(outputListItem.totalCost, 'rsd')}
                    </InfoTooltip>
                  </div>
                </div>
                <div className="col">
                  <div className="bg-item center">
                    <span>
                      {FormatDateShort(outputListItem.date)}
                    </span>
                  </div>
                </div>
                <div className="col">
                  <div className="bg-item center">
                    <span>{outputListItem.takenAwayByDriverAndVehicle}</span>
                  </div>
                </div>
                <div className="col">
                  {outputListItem.orderNumberWithinOutput === 1
                    ? (<div className={`bg-item center ${outputListItem.projectOverride ? "projectOverrideFirst" : ""}`}>
                      <InfoTooltip tooltip={whereForReal(outputListItem)} element="span">
                        {whereOnBill(outputListItem)}
                      </InfoTooltip>
                      {hasEditButton &&
                        <span
                          onClick={(e) => {
                            dispatch(setEditDetailsPopover({
                              isOpen: true,
                              left: e.clientX,
                              top: e.clientY,
                              offsetLeft: -490,
                              offsetTop: -100
                            }));
                            dispatch(getOutputForEdit(outputListItem.outputId));
                          }}
                        >
                          <CiEdit style={{ cursor: "pointer", fontSize: 15, color: grey[500] }} />
                        </span>}
                    </div>)
                    : (
                      <div className={`bg-item bg-transparent right
                        destinationRemainingItems ${outputListItem.projectOverride ? "projectOverrideFirst" : ""}`}>
                        <span>
                          {whereOnBill(outputListItem)}
                        </span>
                      </div>)}
                </div>
                <div className="col">
                  <div className="bg-item center">
                    <InfoTooltip tooltip={outputListItem.supplier} element="span">
                      {outputListItem.supplier}
                    </InfoTooltip>
                  </div>
                </div>
                <div className="col">
                  <input className={`bg-item bg-transparent${hasWriteAccessTo(user, STOCK_MODULE_NAME) ? ' hoverBorder' : ''}`} type="text"
                    value={outputListItem.note}
                    onChange={(e) => {
                      onChangeBasicField({ outputId: outputListItem.outputId, outputItemId: outputListItem.outputItemId, note: e.target.value })
                    }}
                    disabled={!hasWriteAccessTo(user, STOCK_MODULE_NAME)}
                  />

                </div>
                <div className="col center">
                  {hasDeleteButton
                    ? (
                      <span
                        onClick={(e) => {
                          e.stopPropagation();
                          dispatch(requestDeletingOutput(outputListItem));
                        }}
                      >
                        <CancelIcon
                          style={{ cursor: "pointer", fontSize: 15, color: grey[300] }}
                        />
                      </span>
                    )
                    : (<></>)}
                </div>
                <div className="col center">
                  {hasEditButton
                    ? (
                      <span
                        onClick={(e) => {
                          dispatch(setEditPopover({
                            isOpen: true,
                            left: e.clientX,
                            top: e.clientY,
                            offsetLeft: -490,
                            offsetTop: -100
                          }));
                          dispatch(getOutputForEdit(outputListItem.outputId));

                        }}
                      >
                        <CiEdit style={{ cursor: "pointer", fontSize: 15, color: grey[500] }} />
                      </span>
                    )
                    : (<></>)}
                </div>
                <div className="col">
                  <div className="bg-item bg-transparent left">
                    <span className="light">{outputListItem.referenceNumber}</span>
                  </div>
                </div>
              </div>
            );
          })}
      </div>

      <div ref={componentRefRequisition} className="print-area">
        <PrintRequisition doPrint={doPrintRequisition} />
      </div>
      <div ref={componentRefBillOfLanding} className="print-area">
        <PrintBillOfLanding doPrint={doPrintBillOfLanding} />
      </div>
      <Popover
        isOpen={returnPopover.isOpen}
        onClose={handleCloseReturnOutputPopover}
        title="Povraćaj"
        direction="right"
        arrowStyle={{ top: "90px" }}
        position={{
          top: returnPopover.top,
          left: returnPopover.left,
          offsetLeft: returnPopover.offsetLeft,
          offsetTop: returnPopover.offsetTop
        }}
      >
        <ReturnOutput
          inputValues={inputValues}
          setInputValues={setInputValues}
          note={outputItemNote}
          setNote={setOutputItemNote}
          skipCreatingReturnOutput={skipCreatingReturnOutput}
          setSkipCreatingReturnOutput={setSkipCreatingReturnOutput}
          dispatch={dispatch}
          length={outputListItems.length}
          setClickOutsideActive={setClickOutsideReturnPopoverActive} />
      </Popover>
      <Popover
        isOpen={editPopover.isOpen}
        onClose={handleCloseEditOutputPopover}
        title="Izmena"
        direction="left"
        arrowStyle={{ top: "90px" }}
        position={{
          top: editPopover.top,
          left: editPopover.left,
          offsetLeft: editPopover.offsetLeft,
          offsetTop: editPopover.offsetTop
        }}
      >
        <EditOutput
          dispatch={dispatch}
          setClickOutsideActive={setClickOutsideEditPopoverActive} />
      </Popover>
      <Popover
        isOpen={editDetailsPopover.isOpen}
        onClose={handleCloseEditDetailsPopover}
        title="Izmena projekta"
        direction="left"
        arrowStyle={{ top: "90px" }}
        position={{
          top: editDetailsPopover.top,
          left: editDetailsPopover.left,
          offsetLeft: editDetailsPopover.offsetLeft,
          offsetTop: editDetailsPopover.offsetTop
        }}
      >
        <EditOutputDetails
          dispatch={dispatch}
          setClickOutsideActive={setClickOutsideEditDetailsPopoverActive} />
      </Popover>
      <DeleteOutputModal />
    </>
  );
};

export default TopSection;
